import type {
  AssetVersionItemDto,
  AudioVideoMetadataDto,
  DocumentMetadataDto,
  ImageMetadataDto
} from '@api/Api';
import { checkCanPlaySource } from '@helpers/checkCanPlaySource';

type AssetGroup = 'video' | 'audio' | 'document' | 'image';

const TYPE_TO_GROUP: Record<string, AssetGroup> = {
  video: 'video',
  audio: 'audio',
  document: 'document',
  presentation: 'document',
  spreadsheet: 'document',
  raster_image: 'image',
  camera_raw_image: 'image',
  vector_image: 'image'
};

const getGroup = (type?: string): AssetGroup =>
  (type && TYPE_TO_GROUP[type]) || 'image';

const getKnownGroup = (type?: string): AssetGroup | undefined =>
  type ? TYPE_TO_GROUP[type] : undefined;

const INVALID_MESSAGES: Record<AssetGroup, string> = {
  video: 'Video comparison works only with another video.',
  audio: 'Audio comparison works only with another audio.',
  document: 'Document comparison works only with another document.',
  image: 'Image comparison works only with another image.'
};

const PREPARING_MESSAGES: Record<AssetGroup, string> = {
  video: 'Preparing video for comparison…',
  audio: 'Preparing audio for comparison…',
  document: 'Preparing document for comparison…',
  image: 'Preparing image for comparison…'
};

export function areComparableAssetTypes(
  left?: string,
  right?: string
): boolean {
  if (!left || !right) return true;
  if (left === right) return true;
  const gl = getKnownGroup(left);
  const gr = getKnownGroup(right);
  if (gl && gr) return gl === gr;
  return false;
}

export function unconfirmedComparisonMessage(type: string): string {
  return INVALID_MESSAGES[getGroup(type)];
}

function preparingMessage(type: string): string {
  return PREPARING_MESSAGES[getGroup(type)];
}

function getProxyState(status: string) {
  return {
    isPending: status === 'pending',
    isReady: status === 'ready'
  };
}

function isVideoPlaybackPending(version: AssetVersionItemDto): boolean {
  const proxies = Object.values(
    (version.metadata as AudioVideoMetadataDto).videoProxies
  );

  if (checkCanPlaySource(version)) return false;

  const hasReady = proxies.some((x) => x.status === 'ready');
  const hasPending = proxies.some((x) => x.status === 'pending');

  return !hasReady && hasPending;
}

function isVideoPlaybackReady(version: AssetVersionItemDto): boolean {
  const proxies = Object.values(
    (version.metadata as AudioVideoMetadataDto).videoProxies
  );

  return (
    proxies.some((x) => x.status === 'ready') || checkCanPlaySource(version)
  );
}

export type ComparisonPanelState = {
  message: string;
  showLoader: boolean;
};

function handleProxy(
  status: string,
  type: string,
  label: 'image' | 'document'
): ComparisonPanelState | null {
  const { isPending, isReady } = getProxyState(status);

  if (isPending) {
    return {
      message: preparingMessage(type),
      showLoader: true
    };
  }

  if (!isReady) {
    return {
      message: `This ${label} is not ready for comparison yet.`,
      showLoader: false
    };
  }

  return null;
}

function getVersionReadiness(
  version: AssetVersionItemDto
): ComparisonPanelState | null {
  const rm = version.metadata;

  if (rm.type === 'audio') {
    const { isPending, isReady } = getProxyState(rm.audioProxy.status);

    if (isPending) {
      return {
        message: preparingMessage(rm.type),
        showLoader: true
      };
    }

    if (!isReady) {
      return {
        message: 'Audio is not ready for comparison yet.',
        showLoader: false
      };
    }
  }

  if (rm.type === 'video') {
    if (isVideoPlaybackPending(version)) {
      return {
        message: preparingMessage('video'),
        showLoader: true
      };
    }

    if (!isVideoPlaybackReady(version)) {
      return {
        message: 'This video is not available for comparison yet.',
        showLoader: false
      };
    }
  }

  if (rm.type === 'audio') return null;

  const group = getKnownGroup(rm.type);
  if (!group) return null;

  if (group === 'image') {
    return handleProxy((rm as ImageMetadataDto).proxy.status, rm.type, 'image');
  }

  if (group === 'document') {
    return handleProxy(
      (rm as DocumentMetadataDto).proxy.status,
      rm.type,
      'document'
    );
  }

  return null;
}

export function getLeftVersionDisplayValidityState(
  version?: AssetVersionItemDto | null
): ComparisonPanelState | null {
  if (!version) {
    return { message: 'Select version to compare', showLoader: false };
  }

  if (version.status === 'ready') return null;

  if (
    version.status === 'analyze_in_progress' ||
    version.status === 'conversion_in_progress'
  ) {
    return {
      message: preparingMessage(version.metadata.type),
      showLoader: true
    };
  }

  return {
    message: 'This version is not ready for comparison yet.',
    showLoader: false
  };
}

export function getRightPanelComparisonState(
  left?: AssetVersionItemDto | null,
  right?: AssetVersionItemDto | null
): ComparisonPanelState | null {
  if (!right) {
    return { message: 'Select version to compare', showLoader: false };
  }

  if (!left) return null;

  if (!areComparableAssetTypes(left.metadata.type, right.metadata.type)) {
    return {
      message: unconfirmedComparisonMessage(right.metadata.type),
      showLoader: false
    };
  }

  return getVersionReadiness(right);
}
